import React from "react";
import { Box, Typography } from "@mui/material";
import { toast } from "react-toastify";
import GenericFormDialog, { Field } from "../GenericFormDialog";
import { getAllPackages, purchasePackage } from "../../services/PackagesService";
import { getErrorMessage } from "../../utils/apiResponse";
import { formatTableDate } from "../../utils/tableDateFormat";
import { SessionPackage } from "./types";
import { formatMoney } from "./classUtils";
import { Option, useUnitOptions } from "./useClassOptions";
import PackagePaymentPromptDialog from "./PackagePaymentPromptDialog";

interface AssignPackageDialogProps {
  open: boolean;
  businessId?: string;
  clientId: string | null;
  clientName?: string;
  onClose: () => void;
  onAssigned?: () => void;
}

/**
 * The client-list counterpart to "Sell Package" on the Packages page — same
 * purchase call, just starting from a fixed client instead of a fixed
 * package, so staff picks which package to assign instead of who to sell to.
 */
const AssignPackageDialog: React.FC<AssignPackageDialogProps> = ({
  open,
  businessId,
  clientId,
  clientName,
  onClose,
  onAssigned,
}) => {
  const [submitting, setSubmitting] = React.useState(false);
  const [packages, setPackages] = React.useState<SessionPackage[]>([]);
  const [paymentPrompt, setPaymentPrompt] = React.useState<{
    invoiceId: string | number | null;
    amount: number;
  } | null>(null);
  const { options: unitOptions } = useUnitOptions(businessId, open);

  React.useEffect(() => {
    if (!open) return;
    let cancelled = false;

    const fetchPackages = async () => {
      try {
        const result = await getAllPackages();
        if (!cancelled) setPackages(result.filter((pkg) => pkg.active !== false));
      } catch (error) {
        if (!cancelled) {
          toast.error(getErrorMessage(error, "Failed to load packages."));
          setPackages([]);
        }
      }
    };

    fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const packageOptions: Option[] = React.useMemo(
    () =>
      packages.map((pkg) => ({
        label: `${pkg.name || "Package"} — ${pkg.session_count ?? "-"} sessions · ${formatMoney(
          pkg.price
        )}`,
        value: String(pkg.id),
      })),
    [packages]
  );

  const fields: Field[] = React.useMemo(
    () => [
      {
        name: "summary",
        label: "",
        colSpan: 2,
        customRender: () => (
          <Box
            sx={{
              border: "1px solid #eee",
              borderRadius: 2,
              p: 1.5,
              mb: 1,
              bgcolor: "#fafafa",
            }}
          >
            <Typography sx={{ fontWeight: 600 }}>
              {clientName || "This client"}
            </Typography>
            <Typography sx={{ fontSize: 13, color: "#777" }}>
              Pick a package to assign — the client is charged immediately.
            </Typography>
          </Box>
        ),
      },
      {
        name: "package_id",
        label: "Package",
        type: "dropdown",
        options: packageOptions,
        searchable: true,
        required: true,
        colSpan: 2,
      },
      {
        name: "unit_id",
        label: "Unit (optional)",
        type: "dropdown",
        options: unitOptions,
        searchable: true,
        colSpan: 2,
      },
    ],
    [clientName, packageOptions, unitOptions]
  );

  const initialValues = React.useMemo(
    () => ({ package_id: "", unit_id: "" }),
    []
  );

  const handleSubmit = async (values: Record<string, any>) => {
    if (!clientId || !businessId || !values.package_id) return;

    setSubmitting(true);
    try {
      const chosenPackage = packages.find(
        (pkg) => String(pkg.id) === String(values.package_id)
      );
      const result = await purchasePackage(String(values.package_id), {
        client_id: clientId,
        business_id: businessId,
        unit_id: values.unit_id ? String(values.unit_id) : undefined,
      });

      const remaining = result?.sessions_remaining ?? chosenPackage?.session_count;
      const expiry = result?.expires_at
        ? ` · expires ${formatTableDate(result.expires_at)}`
        : "";
      toast.success(`Package assigned — ${remaining} sessions available${expiry}`);
      onClose();
      // The purchase is UNPAID until settled — offer to collect it now
      // instead of sending staff to the Invoices screen for it.
      if (result?.invoice_id) {
        setPaymentPrompt({
          invoiceId: result.invoice_id,
          amount: Number(chosenPackage?.price) || 0,
        });
      } else {
        onAssigned?.();
      }
    } catch (error) {
      toast.error(getErrorMessage(error, "Failed to assign the package."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <GenericFormDialog
        open={open}
        onClose={onClose}
        title="Assign Package"
        submitLabel="Assign & Charge"
        fields={fields}
        initialValues={initialValues}
        isSubmitting={submitting}
        onSubmit={handleSubmit}
      />

      <PackagePaymentPromptDialog
        open={Boolean(paymentPrompt)}
        invoiceId={paymentPrompt?.invoiceId ?? null}
        amount={paymentPrompt?.amount ?? 0}
        onClose={() => {
          setPaymentPrompt(null);
          onAssigned?.();
        }}
        onPaid={() => {
          setPaymentPrompt(null);
          onAssigned?.();
        }}
      />
    </>
  );
};

export default AssignPackageDialog;
